/**
 * WS-O / AUD-8 LB-3: the ONE re-export verb.
 *
 * Copies a finished run's files from the run folder to a NEW destination the operator picks —
 * byte-for-byte, read from disk, never from retained bytes. Re-export exists only for a
 * DIFFERENT destination now: copying to the same folder the run already wrote to is a no-op
 * that re-marshals every byte across the IPC bridge, which is what "Save to folder" had become
 * once the write-through writer existed.
 *
 * `filter` is how "Approve N selects" survives: the same copy, over a subset.
 */
import type { OutputRecord, PlatformIO, PrintFileRef } from "@genz/platform";
import {
  exportBundle, hydrateOutputBytes, printFilesFromOutputs,
  MOCKUPS_BUNDLE_ROOT, TRANSPARENT_BUNDLE_ROOT, type BundleLogger, type ExportReport,
} from "./bundle.js";
import { getExportPrintFiles } from "../stores/exportPrintFiles.js";

export interface CopyRunInput {
  /** Where the run actually wrote — `useBatchStore.lastSavedPath`. */
  srcRoot: string;
  /** The destination the operator just picked. The copy lands HERE, not in a nested dated
   *  folder inside it: the operator already chose the shape of the folder they want. */
  destDir: string;
  outputs: OutputRecord[];
  printFiles?: (PrintFileRef & { bytes?: Uint8Array })[];
  /** The staged designs, so a print file with no rendered source still ships the artwork. */
  designs?: readonly { designSlug: string; bytes?: Uint8Array }[];
  logger?: BundleLogger;
  filter?: (o: OutputRecord) => boolean;
}

/** Does `dest` resolve inside `srcRoot`? Compared case-insensitively on a normalised path,
 *  because APFS and NTFS are, and a self-copy there is just as wasteful. */
export function isUnderRoot(dest: string, srcRoot: string): boolean {
  const norm = (p: string) => p.replace(/\\/g, "/").replace(/\/+$/, "").toLowerCase();
  const d = norm(dest);
  const s = norm(srcRoot);
  return d === s || d.startsWith(`${s}/`);
}

/**
 * A self-copy writes a 1000-file run twice for no reason. State it, refuse it, and say which two
 * folders were involved — "nothing happened" with no reason is how an operator learns not to
 * trust a button. Returns the refusal report, or `null` when the destination is genuinely new.
 */
function refuseSelfCopy(
  destDir: string, srcRoot: string, logger?: BundleLogger,
): ExportReport | null {
  if (!isUnderRoot(destDir, srcRoot)) return null;
  const reason =
    `"${destDir}" is inside the run's own folder ("${srcRoot}"), where these ` +
    `files already are. Pick a different destination — re-exporting to the same place ` +
    `copies every byte for no reason.`;
  logger?.log("error", `Export · copy refused — ${reason}`, { destDir });
  return { written: 0, missing: [{ ref: destDir, reason }], failed: [], collisions: [] };
}

export async function copyRunTo(io: PlatformIO, input: CopyRunInput): Promise<ExportReport> {
  const refused = refuseSelfCopy(input.destDir, input.srcRoot, input.logger);
  if (refused) return refused;

  const chosen = input.filter ? input.outputs.filter(input.filter) : input.outputs;
  // Read every file back from `savedRoot` — the SAME re-read the export path uses, so a run
  // whose bytes the budget dropped copies in full instead of shipping only what is still in
  // RAM. Without `savedRoot` a record is left untouched and `exportBundle` reports it as
  // `missing`, naming the path it tried: never a silently short copy.
  const outputs = await hydrateOutputBytes(
    io,
    chosen.map((o) => ({ ...o, savedRoot: o.savedRoot ?? input.srcRoot })),
  );
  // WS-Y: a copy is a re-export, so it ships what a run ships — the mock-ups. With print files
  // off neither the derivation nor the disk re-read runs, so a 1000-design copy does not pay
  // 1000 reads for bytes `exportBundle` would drop anyway.
  const prints = getExportPrintFiles()
    ? await hydrateOutputBytes(
      io,
      printFilesFromOutputs(outputs, input.designs, input.printFiles).map((p) => ({
        ...p,
        savedRoot: p.savedRoot ?? input.srcRoot,
      })),
    )
    : [];

  return exportBundle(io, {
    name: MOCKUPS_BUNDLE_ROOT,
    outputs,
    printFiles: prints,
    destDir: input.destDir,
    // No `runFolder`: the copy lands in the folder the operator just picked, not in a nested
    // dated folder inside it.
    logger: input.logger,
  });
}

export interface CopyOverlaysInput {
  /** The conversion's run folder — `useBatchStore.lastSavedPath`, stamped by overlay 1. */
  srcRoot: string;
  destDir: string;
  /** The overlay paths exactly as the run wrote them, brand prefix included. */
  relPaths: string[];
  logger?: BundleLogger;
}

/**
 * WS-O / O-11: "Copy run to…" for the TRANSPARENT tab — the same verb, over the same
 * mechanism. An overlay run's files are pre-pathed relative paths rather than `OutputRecord`s
 * (WS-I / D-11's `BundleInput.files`), so that is what this takes; the disk re-read, the
 * self-copy refusal and the "the pick IS the folder" rule are `copyRunTo`'s, not a second
 * implementation of them.
 *
 * An overlay that cannot be re-read is left byte-less on purpose: `exportBundle`'s existing
 * byte-less path reports it as `missing`, naming the path it tried. Never a silently short copy.
 */
export async function copyOverlaysTo(
  io: PlatformIO, input: CopyOverlaysInput,
): Promise<ExportReport> {
  const refused = refuseSelfCopy(input.destDir, input.srcRoot, input.logger);
  if (refused) return refused;

  const read = await hydrateOutputBytes(
    io,
    input.relPaths.map((p): { localRef: string; savedRoot: string; bytes?: Uint8Array } =>
      ({ localRef: p, savedRoot: input.srcRoot })),
  );
  return exportBundle(io, {
    name: TRANSPARENT_BUNDLE_ROOT,
    files: read.map((r) => ({ relPath: r.localRef, bytes: r.bytes ?? new Uint8Array() })),
    destDir: input.destDir,
    logger: input.logger,
  });
}
